import {
  allDatabaseColumnTypes,
  canChangeColumnType,
  columnTypeLabel,
} from '@/lib/databases/values';
import {
  createElement,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';

import { Dialog, DialogButton } from '@/components/ui/Dialog.web';
import { anchorBelowElement, Menu, MenuDivider, MenuItem } from '@/components/ui/Menu.web';

function keyedMenuItem(
  key: string,
  props: {
    label: ReactNode;
    icon?: ReactNode;
    onPress: () => void;
    destructive?: boolean;
    disabled?: boolean;
  },
) {
  return createElement(MenuItem, { key, ...props });
}

function keyedMenuDivider(key: string) {
  return createElement(MenuDivider, { key });
}

import type { DatabaseSortClause } from '@/lib/databases/build-query';
import type { DatabaseColumnDefinition } from '@/lib/databases/types';

import type { AggregateFunction } from '@/lib/databases/values';

import {
  IconCalculate,
  IconChangeType,
  IconDelete,
  IconEditProperty,
  IconHide,
  IconMoveLeft,
  IconMoveRight,
  IconRename,
  IconSort,
} from './DatabaseMenuIcons.web';
import {
  applyColumnSort,
  ColumnHeaderSubmenuRow,
  ColumnSortSubmenu,
} from './ColumnSortSubmenu.web';

import {
  CALCULATE_MENU_MIN_WIDTH,
  CalculateMenuPanel,
} from './CalculateMenuPanel.web';
import { ColumnPropertyEditor } from './ColumnPropertyEditor.web';
import { ColumnTypeIcon } from './ColumnTypeIcon.web';
import { ColumnTypePicker } from './ColumnTypePicker.web';

import {
  attachColumnResize,
  type ColumnResizeHandlers,
} from '@/lib/databases/column-resize.web';

const COLUMN_HEADER_IGNORE = '[data-cs-db-column-header="true"]';

function selectInputText(event: Event) {
  const input = event.target as HTMLInputElement | null;
  input?.select();
}

export type ColumnMenuState =
  | { kind: 'closed' }
  | { kind: 'menu'; columnId: string; panel: 'main' | 'type' | 'edit' | 'calculate' }
  | { kind: 'rename'; columnId: string }
  | { kind: 'delete'; columnId: string };

function columnHeaderClassName(base: string, hovered: boolean): string {
  return hovered ? `${base} cs-db-th-hovered` : base;
}

function columnHeaderHoverHandlers(setHovered: (hovered: boolean) => void) {
  return {
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
    onPointerEnter: () => setHovered(true),
    onPointerLeave: () => setHovered(false),
  };
}

type ColumnHeaderProps = {
  column: DatabaseColumnDefinition;
  disabled?: boolean;
  widthPx?: number;
  resizeHandlers?: ColumnResizeHandlers;
  onResizeDragStart?: () => void;
  onResizeDragEnd?: () => void;
  setHeaderRef: (el: HTMLTableCellElement | null) => void;
  onHeaderClick: () => void;
};

export function ColumnHeader({
  column,
  disabled,
  widthPx,
  resizeHandlers,
  onResizeDragStart,
  onResizeDragEnd,
  setHeaderRef,
  onHeaderClick,
}: ColumnHeaderProps) {
  const w = widthPx ?? 120;
  const [hovered, setHovered] = useState(false);
  return createElement(
    'th',
    {
      ref: setHeaderRef,
      'data-cs-db-column-header': 'true',
      className: columnHeaderClassName('cs-db-th cs-db-th-data', hovered),
      style: { width: w, minWidth: w, maxWidth: w },
      onClick: () => {
        if (!disabled) {
          onHeaderClick();
        }
      },
      onMouseDown: (e: MouseEvent) => e.stopPropagation(),
      ...columnHeaderHoverHandlers(setHovered),
    },
    createElement(
      'span',
      { className: 'cs-db-th-label' },
      createElement(ColumnTypeIcon, { type: column.type }),
      createElement('span', { className: 'cs-db-th-label-text' }, column.name),
    ),
    resizeHandlers && !disabled
      ? createElement('span', {
          className: 'cs-db-col-resize-handle',
          role: 'separator',
          'aria-orientation': 'vertical',
          title: 'Drag to resize · Click to fit',
          onMouseDown: (e: MouseEvent) =>
            attachColumnResize(e, w, resizeHandlers, {
              onDragStart: onResizeDragStart,
              onDragEnd: onResizeDragEnd,
            }),
          onClick: (e: MouseEvent) => e.stopPropagation(),
        })
      : null,
  );
}

export function TitleColumnHeader({
  disabled,
  widthPx,
  frozen,
  resizeHandlers,
  onResizeDragStart,
  onResizeDragEnd,
}: {
  disabled?: boolean;
  widthPx?: number;
  frozen?: boolean;
  resizeHandlers?: ColumnResizeHandlers;
  onResizeDragStart?: () => void;
  onResizeDragEnd?: () => void;
}) {
  const w = widthPx ?? 160;
  const [hovered, setHovered] = useState(false);
  const baseClass = frozen ? 'cs-db-th cs-db-th-title cs-db-th-frozen' : 'cs-db-th cs-db-th-title';
  return createElement(
    'th',
    {
      className: columnHeaderClassName(baseClass, hovered),
      style: { width: w, minWidth: w, maxWidth: w },
      onMouseDown: (e: MouseEvent) => e.stopPropagation(),
      ...columnHeaderHoverHandlers(setHovered),
    },
    createElement(
      'span',
      { className: 'cs-db-th-label' },
      createElement(ColumnTypeIcon, { type: 'title' }),
      createElement('span', { className: 'cs-db-th-label-text' }, 'Title'),
    ),
    resizeHandlers && !disabled
      ? createElement('span', {
          className: 'cs-db-col-resize-handle',
          role: 'separator',
          'aria-orientation': 'vertical',
          title: 'Drag to resize · Click to fit all columns',
          onMouseDown: (e: MouseEvent) =>
            attachColumnResize(e, w, resizeHandlers, {
              onDragStart: onResizeDragStart,
              onDragEnd: onResizeDragEnd,
            }),
          onClick: (e: MouseEvent) => e.stopPropagation(),
        })
      : null,
  );
}

type ColumnMenusHostProps = {
  columns: DatabaseColumnDefinition[];
  state: ColumnMenuState;
  setState: (state: ColumnMenuState) => void;
  headerRefs: MutableRefObject<Map<string, HTMLTableCellElement>>;
  disabled?: boolean;
  onRename: (columnId: string, name: string) => void;
  onDelete: (columnId: string) => void;
  onChangeType: (columnId: string, type: string) => void;
  onMoveLeft: (columnId: string) => void;
  onMoveRight: (columnId: string) => void;
  onHide?: (columnId: string) => void;
  onUpdateColumnConfig?: (columnId: string, config: Record<string, unknown>) => void;
  columnAggregates?: Record<string, AggregateFunction>;
  onAggregateChange?: (propertyId: string, fn: AggregateFunction | null) => void;
  sorts?: DatabaseSortClause[];
  onSortsChange?: (sorts: DatabaseSortClause[]) => void;
};

export function ColumnMenusHost({
  columns,
  state,
  setState,
  headerRefs,
  disabled,
  onRename,
  onDelete,
  onChangeType,
  onMoveLeft,
  onMoveRight,
  onHide,
  onUpdateColumnConfig,
  columnAggregates,
  onAggregateChange,
  sorts,
  onSortsChange,
}: ColumnMenusHostProps) {
  const [hoverSubmenu, setHoverSubmenu] = useState<'sort' | null>(null);
  const sortRowRef = useRef<HTMLButtonElement | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const renameColumnId =
    state.kind === 'rename' ? state.columnId : null;

  useEffect(() => {
    if (renameColumnId) {
      const col = columns.find((c) => c.id === renameColumnId);
      setRenameDraft(col?.name ?? '');
    }
  }, [renameColumnId, columns]);

  function closeMenus() {
    setState({ kind: 'closed' });
    setHoverSubmenu(null);
  }

  const menuColumnId = state.kind === 'menu' ? state.columnId : null;
  const menuColumn = menuColumnId
    ? columns.find((c) => c.id === menuColumnId)
    : null;
  const menuIndex = menuColumn
    ? columns.findIndex((c) => c.id === menuColumn.id)
    : -1;
  const anchorEl = menuColumnId ? headerRefs.current.get(menuColumnId) : null;
  const anchor = anchorEl ? anchorBelowElement(anchorEl) : null;

  const deleteColumn =
    state.kind === 'delete'
      ? columns.find((c) => c.id === state.columnId)
      : null;

  return createElement(
    'div',
    null,
    createElement(Menu, {
      open: state.kind === 'menu' && state.panel === 'main' && !!menuColumn,
      anchor,
      onClose: closeMenus,
      dataAttribute: 'cs-column-menu',
      outsideIgnoreSelector: COLUMN_HEADER_IGNORE,
      children: menuColumn
        ? [
            keyedMenuItem('rename', {
              label: 'Rename',
              icon: IconRename(),
              onPress: () => {
                setState({ kind: 'rename', columnId: menuColumn.id });
              },
            }),
            canChangeColumnType(menuColumn)
              ? keyedMenuItem('change-type', {
                  label: 'Change type',
                  icon: IconChangeType(),
                  onPress: () => {
                    setState({ kind: 'menu', columnId: menuColumn.id, panel: 'type' });
                  },
                })
              : null,
            onUpdateColumnConfig &&
            (menuColumn.type === 'select' ||
              menuColumn.type === 'multi_select' ||
              menuColumn.type === 'date' ||
              menuColumn.type === 'relation' ||
              menuColumn.type === 'rollup')
              ? keyedMenuItem('edit-property', {
                  label: 'Edit property',
                  icon: IconEditProperty(),
                  onPress: () => {
                    setState({ kind: 'menu', columnId: menuColumn.id, panel: 'edit' });
                  },
                })
              : null,
            menuIndex > 0
              ? keyedMenuItem('move-left', {
                  label: 'Move left',
                  icon: IconMoveLeft(),
                  onPress: () => {
                    closeMenus();
                    onMoveLeft(menuColumn.id);
                  },
                })
              : null,
            menuIndex >= 0 && menuIndex < columns.length - 1
              ? keyedMenuItem('move-right', {
                  label: 'Move right',
                  icon: IconMoveRight(),
                  onPress: () => {
                    closeMenus();
                    onMoveRight(menuColumn.id);
                  },
                })
              : null,
            onHide
              ? keyedMenuItem('hide', {
                  label: 'Hide',
                  icon: IconHide(),
                  onPress: () => {
                    closeMenus();
                    onHide(menuColumn.id);
                  },
                })
              : null,
            onSortsChange
              ? createElement(ColumnHeaderSubmenuRow, {
                  key: 'sort-submenu',
                  label: 'Sort',
                  icon: IconSort(),
                  active: hoverSubmenu === 'sort',
                  buttonRef: (el: HTMLButtonElement | null) => {
                    sortRowRef.current = el;
                  },
                  onHover: () => setHoverSubmenu('sort'),
                })
              : null,
            onAggregateChange && menuColumn.type !== 'rollup'
              ? keyedMenuItem('calculate', {
                  label: 'Calculate',
                  icon: IconCalculate(),
                  onPress: () => {
                    setState({ kind: 'menu', columnId: menuColumn.id, panel: 'calculate' });
                  },
                })
              : null,
            keyedMenuDivider('divider'),
            keyedMenuItem('delete', {
              label: 'Delete column',
              icon: IconDelete(),
              destructive: true,
              onPress: () => {
                setState({ kind: 'delete', columnId: menuColumn.id });
              },
            }),
          ]
        : [],
    }),
    state.kind === 'menu' &&
    state.panel === 'main' &&
    hoverSubmenu === 'sort' &&
    menuColumn &&
    onSortsChange
      ? createElement(ColumnSortSubmenu, {
          anchorRow: sortRowRef.current,
          columnId: menuColumn.id,
          sorts: sorts ?? [],
          onPointerEnter: () => setHoverSubmenu('sort'),
          onSelect: (direction) => {
            onSortsChange(applyColumnSort(sorts ?? [], menuColumn.id, direction));
            closeMenus();
          },
        })
      : null,
    createElement(Menu, {
      open:
        state.kind === 'menu' &&
        state.panel === 'type' &&
        !!menuColumn &&
        canChangeColumnType(menuColumn),
      anchor,
      onClose: closeMenus,
      minWidth: 140,
      dataAttribute: 'cs-column-menu',
      outsideIgnoreSelector: COLUMN_HEADER_IGNORE,
      children:
        menuColumn && canChangeColumnType(menuColumn)
          ? allDatabaseColumnTypes()
              .filter((type) => type !== 'relation')
              .map((type) =>
                keyedMenuItem(`type-${type}`, {
                  label: createElement(
                    'span',
                    { className: 'cs-db-menu-type-label' },
                    createElement(ColumnTypeIcon, { type }),
                    columnTypeLabel(type),
                  ),
                  onPress: () => {
                    closeMenus();
                    onChangeType(menuColumn.id, type);
                  },
                }),
              )
        : [],
    }),
    createElement(Menu, {
      open: state.kind === 'menu' && state.panel === 'edit' && !!menuColumn,
      anchor,
      onClose: closeMenus,
      minWidth: 280,
      dataAttribute: 'cs-column-menu',
      outsideIgnoreSelector: COLUMN_HEADER_IGNORE,
      children: menuColumn && onUpdateColumnConfig
        ? createElement(
            'div',
            { className: 'cs-db-column-edit-menu' },
            createElement(ColumnPropertyEditor, {
              column: menuColumn,
              allColumns: columns,
              disabled,
              onSaveConfig: (config) => onUpdateColumnConfig(menuColumn.id, config),
            }),
          )
        : null,
    }),
    createElement(Menu, {
      open: state.kind === 'menu' && state.panel === 'calculate' && !!menuColumn,
      anchor,
      onClose: closeMenus,
      minWidth: CALCULATE_MENU_MIN_WIDTH,
      dataAttribute: 'cs-footer-calculate',
      outsideIgnoreSelector: COLUMN_HEADER_IGNORE,
      children:
        menuColumn && onAggregateChange
          ? createElement(CalculateMenuPanel, {
              columnType: menuColumn.type,
              selectedFn: columnAggregates?.[menuColumn.id],
              submenuSide: 'right',
              onSelect: (fn) => {
                onAggregateChange(menuColumn.id, fn);
                closeMenus();
              },
            })
          : null,
    }),
    createElement(Dialog, {
      open: state.kind === 'rename' && !!renameColumnId,
      title: 'Rename column',
      onClose: closeMenus,
      children: createElement(
        'div',
        { className: 'cs-db-popover-field' },
        createElement('label', null, 'Name'),
        createElement('input', {
          type: 'text',
          value: renameDraft,
          autoFocus: true,
          onFocus: selectInputText,
          onChange: (e: Event) =>
            setRenameDraft((e.target as HTMLInputElement).value),
          onKeyDown: (e: KeyboardEvent) => {
            if (e.key === 'Enter' && renameDraft.trim() && renameColumnId) {
              onRename(renameColumnId, renameDraft.trim());
              closeMenus();
            }
          },
        }),
      ),
      footer: [
        DialogButton({ label: 'Cancel', onPress: closeMenus }),
        DialogButton({
          label: 'Save',
          variant: 'primary',
          disabled: !renameDraft.trim(),
          onPress: () => {
            if (renameDraft.trim() && renameColumnId) {
              onRename(renameColumnId, renameDraft.trim());
              closeMenus();
            }
          },
        }),
      ],
    }),
    createElement(Dialog, {
      open: state.kind === 'delete' && !!deleteColumn,
      title: 'Delete column',
      onClose: closeMenus,
      children: deleteColumn
        ? createElement(
            'p',
            { className: 'text-sm text-fg-muted' },
            `Delete "${deleteColumn.name}"? Cell data for this column will no longer be shown.`,
          )
        : null,
      footer: [
        DialogButton({ label: 'Cancel', onPress: closeMenus }),
        DialogButton({
          label: 'Delete',
          variant: 'danger',
          onPress: () => {
            if (deleteColumn) {
              onDelete(deleteColumn.id);
              closeMenus();
            }
          },
        }),
      ],
    }),
  );
}

export function AddColumnButton({
  disabled,
  onAdd,
  onBeforeOpen,
}: {
  disabled?: boolean;
  onAdd: (name: string, type: string) => void;
  onBeforeOpen?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState('text');

  function openAddDialog() {
    onBeforeOpen?.();
    setName('');
    setType('text');
    setOpen(true);
  }

  function submit() {
    const trimmed = name.trim();
    if (!trimmed) {
      return;
    }
    onAdd(trimmed, type);
    setOpen(false);
    setName('');
    setType('text');
  }

  return createElement(
    'div',
    { className: 'cs-db-add-column' },
    createElement(
      'button',
      {
        type: 'button',
        className: 'cs-db-toolbar-add-btn',
        disabled,
        title: 'Add column',
        'aria-label': 'Add column',
        onMouseDown: (e: MouseEvent) => e.stopPropagation(),
        onClick: openAddDialog,
      },
      '+',
    ),
    createElement(Dialog, {
      open,
      title: 'Add column',
      onClose: () => setOpen(false),
      children: createElement(
        'div',
        { className: 'cs-db-popover-field' },
        createElement(
          'div',
          null,
          createElement('label', null, 'Name'),
          createElement('input', {
            type: 'text',
            value: name,
            placeholder: 'Column name',
            autoFocus: true,
            onFocus: selectInputText,
            onChange: (e: Event) => setName((e.target as HTMLInputElement).value),
            onKeyDown: (e: KeyboardEvent) => {
              if (e.key === 'Enter') {
                submit();
              }
            },
          }),
        ),
        createElement(
          'div',
          { className: 'cs-db-popover-field-type' },
          createElement('label', null, 'Type'),
          createElement(ColumnTypePicker, {
            value: type,
            onChange: setType,
          }),
        ),
      ),
      footer: [
        DialogButton({ label: 'Cancel', onPress: () => setOpen(false) }),
        DialogButton({
          label: 'Add',
          variant: 'primary',
          disabled: !name.trim(),
          onPress: submit,
        }),
      ],
    }),
  );
}
